import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { ALL_API_KEY_SCOPES } from '@courseai/domain';
import { useT } from '../i18n';
import {
  CREATE_API_KEY,
  MY_API_KEYS,
  REVOKE_API_KEY,
  type ApiKeyView,
  type CreateApiKeyData,
  type CreateApiKeyVariables,
  type MyApiKeysData,
  type RevokeApiKeyData,
  type RevokeApiKeyVariables,
} from '../graphql/api-keys';

// API-keys management screen: create per-user keys (name + operation scopes +
// optional project restriction + expiry), see the plaintext ONCE at creation,
// list existing keys, and revoke. Signed-in Cognito users only.
export function ApiKeys(): ReactNode {
  const { t } = useT();
  const { data, loading, error, refetch } = useQuery<MyApiKeysData>(MY_API_KEYS, {
    fetchPolicy: 'cache-and-network',
  });
  const [showForm, setShowForm] = useState(false);
  const [createdPlaintext, setCreatedPlaintext] = useState<string | undefined>(undefined);

  const [revokeApiKey] = useMutation<RevokeApiKeyData, RevokeApiKeyVariables>(REVOKE_API_KEY);

  const keys = data?.myApiKeys ?? [];

  if (loading && data === undefined) {
    return (
      <section className="screen">
        <p>{t('apiKeys.loading')}</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="screen">
        <div className="screen-header">
          <h2>{t('apiKeys.title')}</h2>
          <button type="button" onClick={() => void refetch()}>
            {t('apiKeys.retry')}
          </button>
        </div>
        <p className="form-error">{t('apiKeys.loadError', { message: error.message })}</p>
      </section>
    );
  }

  return (
    <section className="screen">
      <div className="screen-header">
        <h2>{t('apiKeys.title')}</h2>
        <button type="button" onClick={() => setShowForm(true)}>
          {t('apiKeys.create')}
        </button>
      </div>
      <p className="screen-subtitle">{t('apiKeys.subtitle')}</p>

      {createdPlaintext !== undefined && (
        <div className="api-key-reveal" role="status">
          <p className="api-key-reveal-warning">{t('apiKeys.revealWarning')}</p>
          <code className="api-key-reveal-value">{createdPlaintext}</code>
          <div className="modal-actions">
            <button
              type="button"
              onClick={() => void navigator.clipboard?.writeText(createdPlaintext)}
            >
              {t('apiKeys.copy')}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setCreatedPlaintext(undefined)}
            >
              {t('apiKeys.dismiss')}
            </button>
          </div>
        </div>
      )}

      {keys.length === 0 ? (
        <div className="empty-state">
          <p>{t('apiKeys.empty')}</p>
        </div>
      ) : (
        <ul className="api-key-list">
          {keys.map((key) => (
            <ApiKeyRow
              key={key.id}
              apiKey={key}
              onRevoke={async () => {
                await revokeApiKey({ variables: { input: { keyId: key.id } } });
                await refetch();
              }}
            />
          ))}
        </ul>
      )}

      {showForm && (
        <CreateApiKeyForm
          onClose={() => setShowForm(false)}
          onCreated={async (plaintext) => {
            setShowForm(false);
            setCreatedPlaintext(plaintext);
            await refetch();
          }}
        />
      )}
    </section>
  );
}

interface ApiKeyRowProps {
  apiKey: ApiKeyView;
  onRevoke(): Promise<void> | void;
}

function ApiKeyRow({ apiKey, onRevoke }: ApiKeyRowProps): ReactNode {
  const { t } = useT();
  return (
    <li className="api-key-card">
      <div className="api-key-card-head">
        <span className="api-key-card-name">{apiKey.name}</span>
        <span className="api-key-card-masked">{apiKey.maskedKey}</span>
        {apiKey.revoked && <span className="api-key-card-revoked">{t('apiKeys.revoked')}</span>}
      </div>
      <p className="api-key-card-scopes">
        {t('apiKeys.scopesLabel')}: {apiKey.scopes.join(', ') || t('apiKeys.none')}
      </p>
      <p className="api-key-card-projects">
        {t('apiKeys.projectsLabel')}:{' '}
        {apiKey.projectIds.length > 0 ? apiKey.projectIds.join(', ') : t('apiKeys.allProjects')}
      </p>
      <p className="api-key-card-meta">
        {t('apiKeys.createdLabel')}: {apiKey.createdAt}
        {apiKey.expiresAt ? ` · ${t('apiKeys.expiresLabel')}: ${apiKey.expiresAt}` : ''}
        {apiKey.lastUsedAt ? ` · ${t('apiKeys.lastUsedLabel')}: ${apiKey.lastUsedAt}` : ''}
      </p>
      {!apiKey.revoked && (
        <button type="button" className="btn-secondary" onClick={() => void onRevoke()}>
          {t('apiKeys.revoke')}
        </button>
      )}
    </li>
  );
}

interface CreateApiKeyFormProps {
  onClose(): void;
  onCreated(plaintextKey: string): Promise<void> | void;
}

// Modal create-key form: name, scope checkboxes (from the shared vocabulary),
// optional comma-separated project ids, and optional expiry date.
function CreateApiKeyForm({ onClose, onCreated }: CreateApiKeyFormProps): ReactNode {
  const { t } = useT();
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<readonly string[]>([]);
  const [projectIdsText, setProjectIdsText] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  const [createApiKey, { loading }] = useMutation<CreateApiKeyData, CreateApiKeyVariables>(
    CREATE_API_KEY,
  );

  function toggleScope(scope: string): void {
    setSelectedScopes((current) =>
      current.indexOf(scope) === -1
        ? [...current, scope]
        : current.filter((existing) => existing !== scope),
    );
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitError(undefined);
    if (name.trim().length === 0) {
      setSubmitError(t('apiKeys.nameRequired'));
      return;
    }
    if (selectedScopes.length === 0) {
      setSubmitError(t('apiKeys.scopeRequired'));
      return;
    }
    const projectIds = projectIdsText
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id.length > 0);
    try {
      const result = await createApiKey({
        variables: {
          input: {
            name: name.trim(),
            scopes: selectedScopes,
            ...(projectIds.length > 0 ? { projectIds } : {}),
            ...(expiresAt.length > 0 ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
          },
        },
      });
      const plaintext = result.data?.createApiKey.plaintextKey;
      if (plaintext !== undefined) {
        await onCreated(plaintext);
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('apiKeys.createError'));
    }
  }

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={t('apiKeys.create')}
    >
      <form className="modal-card" onSubmit={handleSubmit}>
        <h3>{t('apiKeys.create')}</h3>
        <label>
          {t('apiKeys.name')}
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
          />
        </label>

        <fieldset className="api-key-scopes">
          <legend>{t('apiKeys.scopesLabel')}</legend>
          {ALL_API_KEY_SCOPES.map((scope) => (
            <label key={scope} className="api-key-scope-option">
              <input
                type="checkbox"
                checked={selectedScopes.indexOf(scope) !== -1}
                onChange={() => toggleScope(scope)}
              />
              {scope}
            </label>
          ))}
        </fieldset>

        <label>
          {t('apiKeys.projectsField')}
          <input
            type="text"
            value={projectIdsText}
            onChange={(e) => setProjectIdsText(e.target.value)}
            placeholder={t('apiKeys.projectsPlaceholder')}
          />
        </label>
        <p className="field-hint">{t('apiKeys.projectsHint')}</p>

        <label>
          {t('apiKeys.expiresField')}
          <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
        </label>

        {submitError && <p className="form-error">{submitError}</p>}

        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
            {t('apiKeys.cancel')}
          </button>
          <button type="submit" disabled={loading}>
            {loading ? t('apiKeys.creating') : t('apiKeys.create')}
          </button>
        </div>
      </form>
    </div>
  );
}
